import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDate } from 'app/helpers/formatDate';
import { InterestRegisterFacadeService } from 'app/services/api/interest-register/interest-register.service';
import {
    INTEREST_REGISTER_DETAIL_SECTIONS,
    InterestRegisterDetailFieldConfig,
} from 'common/constants/assists-interest-register-detail.constants';
import {
    InterestRegisterAttachment,
    InterestRegisterDetail,
    InterestRegisterEntryType,
} from 'common/constants/assists-interest-register.constants';

interface InterestRegisterDetailDisplayField {
    labelKey: string;
    value: string;
}

interface InterestRegisterDetailDisplaySection {
    titleKey: string;
    fields: InterestRegisterDetailDisplayField[];
}

@Component({
    selector: 'app-interest-register-detail',
    templateUrl: './interest-register-detail.component.html',
    styleUrls: ['./interest-register-detail.component.scss'],
})
export class InterestRegisterDetailComponent implements OnInit {
    detailSections: InterestRegisterDetailDisplaySection[] = [];
    entry: InterestRegisterDetail | null = null;
    errorTranslationKey: string | null = null;
    isLoading = true;
    isUpdatingReviewed = false;
    canReview = false;

    InterestRegisterEntryType = InterestRegisterEntryType;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly facade: InterestRegisterFacadeService
    ) {}

    ngOnInit(): void {
        this.canReview = this.router.url.includes('/hagsmunaskra/skraningar/');
        this.loadEntry();
    }

    private loadEntry(): void {
        const id = this.route.snapshot.paramMap.get('id');

        if (!id) {
            this.entry = null;
            this.errorTranslationKey = 'label_registration_not_found';
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.errorTranslationKey = null;

        this.facade.getEntry(id, this.canReview).subscribe({
            next: (entry) => {
                this.entry = entry;
                this.detailSections = entry ? this.buildDetailSections(entry) : [];
                this.errorTranslationKey = entry ? null : 'label_registration_not_found';
                this.isLoading = false;
                this.isUpdatingReviewed = false;
            },
            error: (error) => {
                this.entry = null;
                this.detailSections = [];
                this.isLoading = false;
                this.isUpdatingReviewed = false;

                if (error?.status === 401 || error?.status === 403) {
                    this.errorTranslationKey = 'text_interest_register_no_access_to_entry';
                    return;
                }

                if (error?.status === 404) {
                    this.errorTranslationKey = 'label_registration_not_found';
                    return;
                }

                this.errorTranslationKey = 'text_interest_register_error_fetching_entry';
            },
        });
    }

    downloadAttachment(attachment: InterestRegisterAttachment): void {
        if (!this.entry) {
            return;
        }

        this.facade.downloadAttachment(this.entry.id, attachment.id, attachment.fileName).subscribe();
    }

    public updateReviewed(isReviewed: boolean): void {
        if (!this.entry || this.isUpdatingReviewed) {
            return;
        }

        this.isUpdatingReviewed = true;

        this.facade.updateReviewed(this.entry.id, isReviewed).subscribe({
            next: (updated) => {
                if (!updated) {
                    this.isUpdatingReviewed = false;
                    return;
                }

                this.loadEntry();
            },
            error: () => {
                this.isUpdatingReviewed = false;
            },
        });
    }

    // Helpers
    private buildDetailSections(entry: InterestRegisterDetail): InterestRegisterDetailDisplaySection[] {
        const sectionConfigs = INTEREST_REGISTER_DETAIL_SECTIONS[entry.type] ?? [];

        return sectionConfigs
            .map((section) => ({
                titleKey: section.titleKey,
                fields: section.fields
                    .map((field) => this.mapDisplayField(entry.formData, field))
                    .filter((field): field is InterestRegisterDetailDisplayField => field !== null),
            }))
            .filter((section) => section.fields.length > 0);
    }

    private mapDisplayField(
        formData: Record<string, unknown>,
        field: InterestRegisterDetailFieldConfig
    ): InterestRegisterDetailDisplayField | null {
        const rawValue = this.getFieldValue(formData, field);

        if (!field.showWhenEmpty && this.isEmptyValue(rawValue)) {
            return null;
        }

        return {
            labelKey: field.labelKey,
            value: this.formatFieldValue(rawValue, field),
        };
    }

    private getFieldValue(formData: Record<string, unknown>, field: InterestRegisterDetailFieldConfig): unknown {
        const keys = [field.key, ...(field.fallbackKeys ?? [])];

        for (const key of keys) {
            const value = formData?.[key];

            if (!this.isEmptyValue(value)) {
                return value;
            }
        }

        return null;
    }

    private mapConfiguredValue(value: unknown, field: InterestRegisterDetailFieldConfig): string | null {
        if (!field.valueMap || this.isEmptyValue(value)) {
            return null;
        }

        const key = String(value);

        return field.valueMap[key] ?? field.valueMap[key.toLowerCase()] ?? null;
    }

    private formatFieldValue(value: unknown, field: InterestRegisterDetailFieldConfig): string {
        if (this.isEmptyValue(value)) {
            return '-';
        }

        const mappedValue = this.mapConfiguredValue(value, field);

        if (mappedValue !== null) {
            return mappedValue;
        }

        switch (field.type) {
            case 'date':
                return this.formatDateOnly(value);
            case 'boolean':
                return this.formatBoolean(value);
            case 'currency':
                return this.formatCurrency(value);
            case 'number':
                return this.formatNumber(value);
            case 'decimal':
                return this.formatDecimal(value);
            default:
                return this.formatTextValue(value);
        }
    }

    private formatTextValue(value: unknown): string {
        if (Array.isArray(value)) {
            return value.join(', ');
        }

        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value);
        }

        return String(value);
    }

    private parseLocaleNumber(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (value === null || value === undefined) {
            return null;
        }

        let text = String(value).trim();

        if (!text) {
            return null;
        }

        text = text.replace(/\s/g, '').replace(/[^\d,.-]/g, '');

        if (!text) {
            return null;
        }

        if (text.includes(',')) {
            text = text.replace(/\./g, '').replace(',', '.');
        } else if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
            text = text.replace(/\./g, '');
        }

        const numberValue = Number(text);

        return Number.isFinite(numberValue) ? numberValue : null;
    }

    private formatCurrency(value: unknown): string {
        const numberValue = this.parseLocaleNumber(value);

        if (numberValue === null) {
            return String(value);
        }

        return `${this.formatIcelandicNumber(numberValue)} kr.`;
    }

    private formatNumber(value: unknown): string {
        const numberValue = this.parseLocaleNumber(value);

        if (numberValue === null) {
            return String(value);
        }

        return this.formatIcelandicNumber(numberValue);
    }

    private formatIcelandicNumber(value: number): string {
        return Math.round(value)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    private formatDecimal(value: unknown): string {
        const numberValue = this.parseLocaleNumber(value);

        if (numberValue === null) {
            return String(value);
        }

        return this.formatIcelandicDecimalNumber(numberValue);
    }

    private formatIcelandicDecimalNumber(value: number): string {
        const roundedValue = Math.round((value + Number.EPSILON) * 1_000_000) / 1_000_000;
        const [integerPart, decimalPart] = roundedValue.toString().split('.');

        const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        if (!decimalPart) {
            return formattedIntegerPart;
        }

        return `${formattedIntegerPart},${decimalPart}`;
    }

    private formatBoolean(value: unknown): string {
        if (value === true || value === 'true' || value === 'True') {
            return 'Já';
        }

        if (value === false || value === 'false' || value === 'False') {
            return 'Nei';
        }

        return String(value);
    }

    private isEmptyValue(value: unknown): boolean {
        return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    }

    private formatDateOnly(value: unknown): string {
        if (this.isEmptyValue(value)) {
            return '-';
        }

        const textValue = String(value);
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(textValue);

        if (match) {
            return `${match[3]}.${match[2]}.${match[1]}`;
        }

        return this.formatTextValue(value);
    }

    goBack(): void {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    formatDate(date: string): string {
        return formatDate(date);
    }

    formatFileSize(size: number): string {
        return `${Math.round(size / 1024)} KB`;
    }
}
